import type React from 'react'
import { Euler, Group, Quaternion, Vector3 } from 'three'

export type FootstepSide = 'left' | 'right'

export const footprintSettings = {
    color: '#555555',
    size: 0.2,
    opacity: 0.3,
    opacityGlow: 0.75,
    lifetime: 0.8,
    zOffset: 0,
    leftFootX: -0.105,
    rightFootX: 0.105,
    cadenceSeconds: 0.17,
    referenceRunSpeed: 12,
    referenceAnimationSpeed: 1.5,
    surfaceOffset: 0.1,
}

export const jumpDustSettings = {
    color: '#fef1e1',
    count: 20,
    size: 0.35,
    endSize: 0.7,
    opacity: 0.85,
    speed: 3.0,
    ringRadius: 0.02,
    ringRadiusJitter: 0.08,
    upwardVelocity: 0.15,
    outwardVelocity: 1.4,
    tangentialScatter: 0,
    verticalScatter: 0.25,
    lifetime: 1.2,
    drag: 9,
    yOffset: 0.2,
}

export const _spawnPos = new Vector3()
export const _playerWorldPos = new Vector3()
export const _worldQuat = new Quaternion()
export const _surfaceQuat = new Quaternion()
export const _forwardVec = new Vector3()
export const _rightVec = new Vector3()
export const _upVec = new Vector3(0, 1, 0)
export const _footprintRotationOffset = new Quaternion().setFromEuler(new Euler(0, 0, Math.PI))

export function getCadenceInterval(runSpeed: number, animationSpeed: number): number {
    const nrs = Math.max(runSpeed / Math.max(footprintSettings.referenceRunSpeed, 0.001), 0.25)
    const nas = Math.max(animationSpeed / Math.max(footprintSettings.referenceAnimationSpeed, 0.001), 0.25)
    return Math.max(0.06, Math.min(0.4, footprintSettings.cadenceSeconds / (nrs * nas)))
}

export type FootstepPlacement = {
    position: Vector3
    surfaceQuat: Quaternion
    worldQuat: Quaternion
    forward: Vector3
    right: Vector3
    up: Vector3
    groundY: number
}

export function computeFootPlacement(
    anchorRef: React.RefObject<Group | null>,
    side: FootstepSide,
): FootstepPlacement | null {
    if (!anchorRef.current) return null

    anchorRef.current.getWorldPosition(_playerWorldPos)
    anchorRef.current.getWorldQuaternion(_worldQuat)

    _rightVec.set(1, 0, 0).applyQuaternion(_worldQuat).normalize()
    _forwardVec.set(0, 0, -1).applyQuaternion(_worldQuat).normalize()
    _upVec.set(0, 1, 0).applyQuaternion(_worldQuat).normalize()

    const footX = side === 'left' ? footprintSettings.leftFootX : footprintSettings.rightFootX

    _spawnPos.copy(_playerWorldPos)
    _spawnPos.addScaledVector(_rightVec, footX)
    _spawnPos.addScaledVector(_forwardVec, footprintSettings.zOffset)
    _spawnPos.addScaledVector(_upVec, footprintSettings.surfaceOffset)

    return {
        position: _spawnPos.clone(),
        surfaceQuat: _surfaceQuat.copy(_worldQuat).multiply(_footprintRotationOffset).clone(),
        worldQuat: _worldQuat.clone(),
        forward: _forwardVec.clone(),
        right: _rightVec.clone(),
        up: _upVec.clone(),
        groundY: _playerWorldPos.y,
    }
}

export type FootstepEffectType = '' | 'dust' | 'leaves' | 'glow'

export const footstepEffectTypeKeys: Exclude<FootstepEffectType, ''>[] = ['dust', 'leaves', 'glow']

export const equipTypeKeys: string[] = ['wings', 'bell', 'glasses', 'helmet', 'vest', 'gloves', 'boots']

// gearIndex must match Character.upgradeItemNames / unlock order:
// 0 boots → 1 gloves → 2 helmet → 3 vest
export const gearItems = [
    { key: 'boots', gearIndex: 0 },
    { key: 'gloves', gearIndex: 1 },
    { key: 'helmet', gearIndex: 2 },
    { key: 'vest', gearIndex: 3 },
]

export const footstepEffectConfigs: Record<Exclude<FootstepEffectType, ''>, { label: string }> = {
    dust: { label: 'Dust' },
    leaves: { label: 'Leaves' },
    glow: { label: 'Glow' },
}

export type FootstepStepEvent = {
    id: number
    side: FootstepSide
    timestamp: number
}

export type FootstepJumpEvent = {
    id: number
    timestamp: number
}

export type FootstepTimelineSnapshot = {
    isActive: boolean
    footstepsEnabled: boolean
    runSpeed: number
    animationSpeed: number
    isJumping: boolean
    steps: FootstepStepEvent[]
    jumps: FootstepJumpEvent[]
}

export type FootstepTimelineRef = React.RefObject<FootstepTimelineSnapshot>
